<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="warning"
      :closable="false"
      title="改费率只影响新接单锁价；已锁价运单完单不变。餐饮开关关闭后存量运单仍履约回调。退款/真打款未在此页。"
    />

    <el-form v-loading="loading" :model="form" label-width="160px" class="ops-form">
      <el-divider content-position="left">结算费率</el-divider>
      <el-form-item label="平台费率">
        <el-input-number v-model="form.platformRate" :min="0" :max="1" :step="0.01" :precision="4" />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]">默认 0.20</span>
      </el-form-item>
      <el-form-item label="默认机构费率">
        <el-input-number v-model="form.defaultOrgRate" :min="0" :max="1" :step="0.01" :precision="4" />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]">默认 0.10；自由池为 0</span>
      </el-form-item>
      <el-form-item label="转单手续费率">
        <el-input-number v-model="form.transferFeeRate" :min="0" :max="1" :step="0.01" :precision="4" />
      </el-form-item>
      <el-form-item label="日免费转单次数">
        <el-input-number v-model="form.transferFreeDaily" :min="0" :max="100" :step="1" />
      </el-form-item>

      <el-divider content-position="left">骑手钱包</el-divider>
      <el-form-item label="日提现限额（元）">
        <el-input-number
          v-model="form.dailyWithdrawLimit"
          :min="1"
          :max="100000"
          :step="50"
          :precision="2"
        />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]"
          >含当日待审+已通过；默认 500</span
        >
      </el-form-item>

      <el-divider content-position="left">配送时效（按公里）</el-divider>
      <el-form-item label="公里档时效">
        <div class="org-rates-block">
          <p class="org-rates-hint">
            进单时按取送直线距离匹配；「公里上限」留空表示以上兜底。仅影响新进单锁时效。
          </p>
          <el-table :data="etaRuleRows" size="small" border empty-text="暂无档位">
            <el-table-column label="公里上限（≤）" width="180">
              <template #default="{ row }">
                <el-input-number
                  v-model="row.maxKm"
                  :min="0.1"
                  :max="9999"
                  :step="0.5"
                  :precision="1"
                  controls-position="right"
                  placeholder="空=以上"
                  class="!w-full"
                />
              </template>
            </el-table-column>
            <el-table-column label="时效（分钟）" width="160">
              <template #default="{ row }">
                <el-input-number
                  v-model="row.minutes"
                  :min="1"
                  :max="999"
                  :step="5"
                  class="!w-full"
                />
              </template>
            </el-table-column>
            <el-table-column label="说明" min-width="120">
              <template #default="{ row }">
                {{ row.maxKm == null ? '以上兜底' : `≤ ${row.maxKm} km` }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="72" align="center">
              <template #default="{ $index }">
                <el-button link type="danger" @click="removeEtaRuleRow($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button class="mt-8px" @click="addEtaRuleRow">+ 添加档位</el-button>
          <el-button class="mt-8px ml-8px" @click="addEtaCatchAllRow">+ 以上兜底</el-button>
        </div>
      </el-form-item>

      <el-form-item label="机构费率覆盖">
        <div class="org-rates-block">
          <p class="org-rates-hint">
            为指定配送机构单独设费率；未添加的机构仍用上方「默认机构费率」。仅影响新接单锁价。
          </p>
          <el-table :data="orgRateRows" size="small" border empty-text="暂无单独覆盖，点击下方添加">
            <el-table-column label="配送机构" min-width="260">
              <template #default="{ row }">
                <el-select
                  v-model="row.orgId"
                  filterable
                  clearable
                  placeholder="选择机构"
                  class="!w-full"
                >
                  <el-option
                    v-for="o in orgOptions"
                    :key="o.orgId"
                    :label="orgOptionLabel(o)"
                    :value="o.orgId"
                  />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="机构费率" width="168">
              <template #default="{ row }">
                <el-input-number
                  v-model="row.rate"
                  :min="0"
                  :max="1"
                  :step="0.01"
                  :precision="4"
                  class="!w-full"
                />
              </template>
            </el-table-column>
            <el-table-column label="约合" width="88" align="center">
              <template #default="{ row }">{{ ratePercentText(row.rate) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="72" align="center">
              <template #default="{ $index }">
                <el-button link type="danger" @click="removeOrgRateRow($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button class="mt-8px" @click="addOrgRateRow">+ 添加机构覆盖</el-button>
        </div>
      </el-form-item>

      <el-divider content-position="left">餐饮接运力开关</el-divider>
      <el-form-item label="出餐进运力">
        <el-switch v-model="form.cateringEnabled" />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]"
          >开：出餐/呼叫骑手建运单；关：出餐成功但不建单（可用呼叫骑手在开闸后补推）</span
        >
      </el-form-item>
      <el-form-item label="关时拒绝出餐">
        <el-switch v-model="form.failWhenDisabled" />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]">严模式：出餐直接失败</span>
      </el-form-item>

      <el-divider content-position="left">自由池容量</el-divider>
      <el-form-item label="软校验开关">
        <el-switch v-model="form.capacityCheckEnabled" />
        <span class="ml-8px text-13px text-[var(--el-text-color-secondary)]">仅打日志，不阻断进抢单池</span>
      </el-form-item>
      <el-form-item label="同城在线自由骑 ≥">
        <el-input-number v-model="form.minOnlineFree" :min="0" :max="100" :step="1" />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
        <el-button @click="load">刷新</el-button>
      </el-form-item>
    </el-form>

    <el-divider content-position="left">变更审计</el-divider>
    <el-table :data="audits" stripe size="small" max-height="360">
      <el-table-column label="时间" width="168">
        <template #default="{ row }">{{ formatCapacityDateTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作者" width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ formatOpsActor(row.actor, actorNameMap) }}</template>
      </el-table-column>
      <el-table-column label="变更内容" min-width="280">
        <template #default="{ row }">
          <template v-if="auditChanges(row.detail).length">
            <div class="audit-summary">{{ summarizeOpsAuditChanges(row.detail) }}</div>
            <el-popover v-if="auditChanges(row.detail).length > 1" placement="top-start" :width="420" trigger="click">
              <template #reference>
                <el-button link type="primary" class="!text-12px !p-0 !h-auto">查看全部</el-button>
              </template>
              <ul class="audit-pop-list">
                <li v-for="(c, i) in auditChanges(row.detail)" :key="i">
                  <span class="audit-pop-label">{{ c.label }}</span>
                  <span class="audit-pop-from">{{ c.from }}</span>
                  <span class="audit-pop-arrow">→</span>
                  <span class="audit-pop-to">{{ c.to }}</span>
                </li>
              </ul>
            </el-popover>
          </template>
          <span v-else class="text-[var(--el-text-color-secondary)]">—</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="auditsTotal"
      v-model:page="auditsPageNo"
      v-model:limit="auditsPageSize"
      @pagination="loadAudits"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as OpsApi from '@/api/capacity/ops'
import * as OrgApi from '@/api/capacity/org'
import { getUserList } from '@/api/system/user'
import { formatCapacityDateTime } from '@/api/capacity/labels'
import { ContentWrap } from '@/components/ContentWrap'
import { ElMessage } from 'element-plus'
import {
  formatOpsActor,
  listOpsAuditChanges,
  parseAdminActorId,
  summarizeOpsAuditChanges
} from './opsAuditDisplay'

defineOptions({ name: 'CapacityOpsSettings' })

const loading = ref(false)
const saving = ref(false)
const form = reactive<OpsApi.OpsConfig>({
  platformRate: 0.2,
  defaultOrgRate: 0.1,
  transferFeeRate: 0.2,
  transferFreeDaily: 5,
  dailyWithdrawLimit: 500,
  cateringEnabled: false,
  failWhenDisabled: false,
  capacityCheckEnabled: true,
  minOnlineFree: 1
})
interface OrgRateRow {
  key: number
  orgId: string
  rate: number
}
interface EtaRuleRow {
  key: number
  maxKm: number | undefined
  minutes: number
}

let orgRateRowKey = 0
let etaRuleRowKey = 0
const orgRateRows = ref<OrgRateRow[]>([])
const etaRuleRows = ref<EtaRuleRow[]>([
  { key: ++etaRuleRowKey, maxKm: 3, minutes: 40 },
  { key: ++etaRuleRowKey, maxKm: 5, minutes: 50 },
  { key: ++etaRuleRowKey, maxKm: undefined, minutes: 60 }
])
const orgOptions = ref<OrgApi.OrgItem[]>([])
const audits = ref<OpsApi.OpsAudit[]>([])
const auditsPageNo = ref(1)
const auditsPageSize = ref(10)
const auditsTotal = ref(0)
const actorNameMap = ref<Record<number, string>>({})

const auditChanges = (detail?: string) => listOpsAuditChanges(detail)

function ratePercentText(rate?: number) {
  const n = Number(rate)
  if (!Number.isFinite(n)) return '—'
  return `${(n * 100).toFixed(2)}%`
}

function orgOptionLabel(o: OrgApi.OrgItem) {
  const name = o.name || o.orgId
  const city = o.city ? ` · ${o.city}` : ''
  return `${name}（${o.orgId}）${city}`
}

function syncOrgRateRows(map?: Record<string, number>) {
  orgRateRowKey = 0
  const entries = Object.entries(map || {})
  orgRateRows.value = entries.map(([orgId, rate]) => ({
    key: ++orgRateRowKey,
    orgId,
    rate: Number(rate)
  }))
}

function syncEtaRuleRows(rules?: OpsApi.EtaKmRule[]) {
  etaRuleRowKey = 0
  const list = rules?.length
    ? rules
    : [
        { maxKm: 3, minutes: 40 },
        { maxKm: 5, minutes: 50 },
        { maxKm: null, minutes: 60 }
      ]
  etaRuleRows.value = list.map((r) => ({
    key: ++etaRuleRowKey,
    maxKm: r.maxKm == null ? undefined : Number(r.maxKm),
    minutes: Number(r.minutes) || 40
  }))
}

function addEtaRuleRow() {
  etaRuleRows.value.push({
    key: ++etaRuleRowKey,
    maxKm: 3,
    minutes: 40
  })
}

function addEtaCatchAllRow() {
  if (etaRuleRows.value.some((r) => r.maxKm == null)) {
    ElMessage.warning('已有「以上」兜底档')
    return
  }
  etaRuleRows.value.push({
    key: ++etaRuleRowKey,
    maxKm: undefined,
    minutes: 60
  })
}

function removeEtaRuleRow(index: number) {
  etaRuleRows.value.splice(index, 1)
}

function buildEtaKmRules(): OpsApi.EtaKmRule[] | null {
  if (!etaRuleRows.value.length) {
    ElMessage.error('请至少配置一档时效')
    return null
  }
  const rules: OpsApi.EtaKmRule[] = []
  let catchAll = 0
  for (const row of etaRuleRows.value) {
    if (row.minutes == null || row.minutes <= 0) {
      ElMessage.error('时效分钟须大于 0')
      return null
    }
    if (row.maxKm == null) {
      catchAll++
      rules.push({ maxKm: null, minutes: row.minutes })
    } else if (row.maxKm <= 0) {
      ElMessage.error('公里上限须大于 0')
      return null
    } else {
      rules.push({ maxKm: row.maxKm, minutes: row.minutes })
    }
  }
  if (catchAll === 0) {
    ElMessage.error('须配置一档「以上」兜底（公里上限留空）')
    return null
  }
  if (catchAll > 1) {
    ElMessage.error('「以上」兜底只能有一档')
    return null
  }
  return rules
}

function addOrgRateRow() {
  orgRateRows.value.push({
    key: ++orgRateRowKey,
    orgId: '',
    rate: form.defaultOrgRate ?? 0.1
  })
}

function removeOrgRateRow(index: number) {
  orgRateRows.value.splice(index, 1)
}

function buildOrgRatesMap(): Record<string, number> | null {
  const map: Record<string, number> = {}
  for (const row of orgRateRows.value) {
    const orgId = row.orgId?.trim()
    if (!orgId) continue
    if (map[orgId] != null) {
      ElMessage.error(`机构「${orgId}」重复配置，请合并或删除多余行`)
      return null
    }
    if (row.rate == null || row.rate < 0) {
      ElMessage.error(`机构「${orgId}」费率无效`)
      return null
    }
    map[orgId] = row.rate
  }
  return map
}

async function loadOrgOptions() {
  try {
    const r = await OrgApi.orgList({ pageNo: 1, pageSize: 200 })
    orgOptions.value = (r?.list || []).filter((o) => o.orgId)
    // 配置里已有、但不在列表中的机构（如已归档）补进下拉，避免只显示 id
    const known = new Set(orgOptions.value.map((o) => o.orgId))
    for (const row of orgRateRows.value) {
      if (row.orgId && !known.has(row.orgId)) {
        orgOptions.value.push({
          orgId: row.orgId,
          name: row.orgId,
          city: '',
          status: 'unknown'
        })
      }
    }
  } catch {
    orgOptions.value = orgRateRows.value
      .filter((r) => r.orgId)
      .map((r) => ({
        orgId: r.orgId,
        name: r.orgId,
        city: '',
        status: 'unknown'
      }))
  }
}

async function resolveActorNames(rows: OpsApi.OpsAudit[]) {
  const ids = [
    ...new Set(
      rows.map((r) => parseAdminActorId(r.actor)).filter((id): id is number => id != null)
    )
  ]
  if (!ids.length) return
  try {
    const users = await getUserList(ids)
    const next = { ...actorNameMap.value }
    for (const u of users || []) {
      if (u?.id) next[u.id] = u.nickname || u.username || `用户${u.id}`
    }
    actorNameMap.value = next
  } catch {
    /* 无权限或接口失败时仍显示 admin:id */
  }
}

const loadAudits = async () => {
  const r = await OpsApi.listOpsAudits({
    pageNo: auditsPageNo.value,
    pageSize: auditsPageSize.value,
  })
  audits.value = r?.list || []
  auditsTotal.value = r?.total || 0
  await resolveActorNames(audits.value)
}

const load = async () => {
  loading.value = true
  try {
    const cfg = await OpsApi.getOpsConfig()
    Object.assign(form, cfg)
    syncOrgRateRows(cfg.orgRates)
    syncEtaRuleRows(cfg.etaKmRules)
    await Promise.all([loadOrgOptions(), loadAudits()])
  } finally {
    loading.value = false
  }
}

const save = async () => {
  const orgRates = buildOrgRatesMap()
  if (orgRates == null) return
  const etaKmRules = buildEtaKmRules()
  if (etaKmRules == null) return
  saving.value = true
  try {
    await OpsApi.saveOpsConfig({ ...form, orgRates, etaKmRules })
    ElMessage.success('已保存')
    await load()
  } catch (e: any) {
    ElMessage.error(e?.msg || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped lang="scss">
.ops-form {
  max-width: 880px;
}
.org-rates-block {
  width: 100%;
  max-width: 640px;
}
.org-rates-hint {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--el-text-color-secondary);
}
.audit-summary {
  font-size: 13px;
  line-height: 1.5;
  color: var(--el-text-color-regular);
}
.audit-pop-list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
  line-height: 1.6;
}
.audit-pop-label {
  display: inline-block;
  min-width: 8em;
  color: var(--el-text-color-secondary);
}
.audit-pop-from {
  color: var(--el-text-color-regular);
}
.audit-pop-arrow {
  margin: 0 6px;
  color: var(--el-text-color-placeholder);
}
.audit-pop-to {
  color: var(--el-color-primary);
  font-weight: 500;
}
</style>
